import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionArticle.module.css';

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show article content.
 * The article content is mainly supposed to be inside a block.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'article'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionArticle = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
          {sectionId === "join-today" &&
            <>
              {/* <div className={css.flipAnimationRight}>
                <svg width="96" height="86" viewBox="0 0 96 86" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M89.2727 31.9133C82.5239 24.2146 72.9932 19.5121 62.7773 18.8404C52.5613 18.1687 42.497 21.5827 34.7982 28.3315C27.0995 35.0803 22.397 44.611 21.7253 54.8269C21.0535 65.0429 24.4676 75.1072 31.2164 82.806L60.2445 57.3597L89.2727 31.9133Z"
                    fill="#FEC220" />
                </svg>
              </div> */}
              <div className={css.flipAnimationLeft}>
                <svg width="96" height="86" viewBox="0 0 96 86" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M89.2727 31.9133C82.5239 24.2146 72.9932 19.5121 62.7773 18.8404C52.5613 18.1687 42.497 21.5827 34.7982 28.3315C27.0995 35.0803 22.397 44.611 21.7253 54.8269C21.0535 65.0429 24.4676 75.1072 31.2164 82.806L60.2445 57.3597L89.2727 31.9133Z"
                    fill="#FEC220" />
                </svg>
              </div>
            </>
          }
          {sectionId ==="join-arabibi-family"&&
            <div className={css.flipAnimationLeft}>
              <svg width="96" height="86" viewBox="0 0 96 86" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M89.2727 31.9133C82.5239 24.2146 72.9932 19.5121 62.7773 18.8404C52.5613 18.1687 42.497 21.5827 34.7982 28.3315C27.0995 35.0803 22.397 44.611 21.7253 54.8269C21.0535 65.0429 24.4676 75.1072 31.2164 82.806L60.2445 57.3597L89.2727 31.9133Z"
                  fill="#FEC220" />
              </svg>
            </div>
          }
           {sectionId ==="start-learning-section"&&
            <div className={css.flipAnimationLeft}>
              <svg width="96" height="86" viewBox="0 0 96 86" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M89.2727 31.9133C82.5239 24.2146 72.9932 19.5121 62.7773 18.8404C52.5613 18.1687 42.497 21.5827 34.7982 28.3315C27.0995 35.0803 22.397 44.611 21.7253 54.8269C21.0535 65.0429 24.4676 75.1072 31.2164 82.806L60.2445 57.3597L89.2727 31.9133Z"
                  fill="#FEC220" />
              </svg>
            </div>
          }
        </header>
      ) : null}
      {hasBlocks ? (
        <div
          className={classNames(defaultClasses.blockContainer, css.articleMain, {
            [css.noSidePaddings]: isInsideContainer,
          })}
        >
          <BlockBuilder
            blocks={blocks}
            sectionId={sectionId}
            ctaButtonClass={defaultClasses.ctaButton}
            options={options}
          />
        </div>
      ) : null}
    </SectionContainer>
  );
};

export default SectionArticle;
